import { Button } from "@/components/ui/button";
import { ShieldCheck, ListChecks, ChatCircleDots } from "@phosphor-icons/react";

const LOGO_URL = "https://customer-assets-gfyr7b9c.emergentagent.net/job_francophone-ops/artifacts/1lh4l6xi_3%20%281%29.PNG";
import { useAuth } from "@/lib/auth";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Info } from "@phosphor-icons/react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const expired = searchParams.get("expired") === "1";
  const denied = searchParams.get("denied");
  const rawReturnTo = searchParams.get("returnTo") || "/dashboard";
  // Open-redirect guard: only accept same-origin relative paths that start with a single "/"
  const returnTo = /^\/(?!\/)/.test(rawReturnTo) ? rawReturnTo : "/dashboard";

  useEffect(() => {
    if (!loading && user) navigate(returnTo, { replace: true });
  }, [user, loading, navigate, returnTo]);

  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + returnTo;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white" data-testid="login-page">
      {/* Left: brand + form */}
      <div className="flex flex-col justify-between p-10 lg:p-16">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Unity Language Group" className="h-11 w-11 object-contain" />
          <div>
            <div className="font-display font-bold text-lg tracking-tight text-[#1E3A8A]">Unity Language Group</div>
            <div className="text-xs text-slate-500 -mt-0.5">USCIS · Medical · Legal Interpretation</div>
          </div>
        </div>

        <div className="max-w-md">
          <div className="text-xs tracking-[0.15em] uppercase text-[#B91C1C] font-semibold mb-4">
            Unity Ops · USCIS · Medical · Legal
          </div>
          {expired && (
            <div
              data-testid="session-expired-banner"
              className="mb-5 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-2"
            >
              <Info size={16} weight="fill" className="text-amber-700 mt-0.5 shrink-0" />
              <div className="text-xs text-amber-900 leading-relaxed">
                <b>Your session expired.</b> Please sign in again — we'll take you back to where you were.
              </div>
            </div>
          )}
          {denied && (
            <div
              data-testid="access-denied-banner"
              className="mb-5 rounded-lg bg-red-50 border border-red-200 px-4 py-3 flex items-start gap-2"
            >
              <Info size={16} weight="fill" className="text-red-700 mt-0.5 shrink-0" />
              <div className="text-xs text-red-900 leading-relaxed">
                <b>Access denied.</b> {denied} If you believe this is a mistake, contact your Unity Language Group admin.
              </div>
            </div>
          )}
          <h1 className="text-4xl sm:text-5xl font-display font-bold tracking-tight text-slate-900 leading-[1.05]">
            One platform for the whole <span className="text-[#1E3A8A]">Unity</span> operation.
          </h1>
          <p className="mt-5 text-slate-600 leading-relaxed">
            Manage clients, 1099 interpreters, projects, communications, and invoicing — all in one place.
            Built for Unity Language Group's francophone assignments across USCIS, medical, and legal work in the US.
          </p>
          <div className="mt-8 flex flex-col gap-3">
            <Button
              onClick={signIn}
              data-testid="login-google-btn"
              className="h-11 px-5 bg-[#1E3A8A] hover:bg-[#1E40AF] text-white font-medium"
            >
              Continue with Google
            </Button>
            <div className="text-xs text-slate-500">
              Secured by Emergent · Team members get role-based access.
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <Feature icon={<ShieldCheck size={18} weight="duotone" />} title="Contracts" text="Client & 1099 contracts, rates, history." />
            <Feature icon={<ListChecks size={18} weight="duotone" />} title="Projects" text="Assign, track status, calendar view." />
            <Feature icon={<ChatCircleDots size={18} weight="duotone" />} title="Comms" text="Emails, calls, notes, AI drafts." />
          </div>
        </div>

        <div className="text-xs text-slate-400 flex items-center gap-2 flex-wrap">
          <span>© {new Date().getFullYear()} Unity Language Group · Interpreter Operations</span>
          <span>·</span>
          <a href="/privacy" className="hover:text-slate-700 underline" data-testid="footer-privacy">Privacy</a>
          <span>·</span>
          <a href="/terms" className="hover:text-slate-700 underline" data-testid="footer-terms">Terms</a>
        </div>
      </div>

      {/* Right: image + subtle overlay */}
      <div
        className="hidden lg:block relative bg-cover bg-center"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGFyY2hpdGVjdHVyYWwlMjBsaW5lc3xlbnwwfHx8fDE3ODQ5MjI0Nzh8MA&ixlib=rb-4.1.0&q=85)",
        }}
      >
        <div className="absolute inset-0 bg-[#0B1120]/60" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-xs tracking-[0.2em] uppercase text-blue-200 font-semibold mb-3">Field Report</div>
          <div className="text-2xl font-display font-semibold leading-snug">
            "Every interpreter, every rate, every USCIS interview — one single source of truth.
            Fewer emails, faster confirmations, cleaner books."
          </div>
          <div className="mt-4 text-sm text-slate-200">— Unity Language Group Operations</div>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon, title, text }) {
  return (
    <div className="border border-slate-200 rounded-md p-3">
      <div className="flex items-center gap-2 text-slate-900 font-medium">
        <span className="text-blue-800">{icon}</span>
        {title}
      </div>
      <div className="text-slate-500 text-xs mt-1 leading-relaxed">{text}</div>
    </div>
  );
}
